/**
 * Brand Knowledge « Leviers de mesure » → Personas (2026-10-11).
 *
 * A `BrandPersona` is the reusable « Qui est-il ? » card a conversation links to. It is
 * NOT part of `BrandStrategy`: every create/edit/delete goes straight to its own API and
 * never touches the page's strategy draft, so it never marks the strategy dirty or stale.
 * Controls, labels and option sets mirror PersonaEditorModal's section 1 so the UX is the
 * same in both places (the labels reuse the `conversations.editor*` keys).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import {
  CONVERSATION_MAX_PERSONA_BUDGET_CHARS,
  CONVERSATION_MAX_PERSONA_DESCRIPTION_CHARS,
  CONVERSATION_MAX_PERSONA_LIST_ITEMS,
  CONVERSATION_MAX_PERSONA_LIST_ITEM_CHARS,
  CONVERSATION_MAX_PERSONA_NAME_CHARS,
  CONVERSATION_MAX_TAGS,
  type BrandPersona,
  type ConversationTone,
} from '@traaker/shared';
import { api, ApiError } from '../../../services/api';
import { K, markSx, microLabel } from './tokens';
import { Highlight } from './Highlight';

type ListedPersona = BrandPersona & { linkedConversations: number };

const TONE_KEYS: ConversationTone[] = ['neutral', 'direct', 'skeptical', 'enthusiastic', 'technical', 'novice'];
const AGE_RANGES = ['18-25 ans', '26-34 ans', '35-44 ans', '45-54 ans', '55 ans et plus'];
const MAX_TAG_CHARS = 30;

type Gender = NonNullable<BrandPersona['gender']>;
type SelectionFocus = NonNullable<BrandPersona['selectionFocus']>;
type DomainExpertise = NonNullable<BrandPersona['domainExpertise']>;
type LanguageStyle = NonNullable<BrandPersona['languageStyle']>;
type Receptiveness = NonNullable<BrandPersona['receptiveness']>;

interface FormState {
  name: string;
  description: string;
  tone: ConversationTone;
  decisionCriteria: string;
  objections: string;
  ageRange: string;
  gender: Gender | '';
  budget: string;
  selectionFocus: SelectionFocus | '';
  domainExpertise: DomainExpertise | '';
  languageStyle: LanguageStyle | '';
  receptiveness: Receptiveness | '';
  tags: string[];
}

function formFrom(p?: BrandPersona): FormState {
  return {
    name: p?.name ?? '',
    description: p?.description ?? '',
    tone: p?.tone ?? 'neutral',
    decisionCriteria: (p?.decisionCriteria ?? []).join('\n'),
    objections: (p?.objections ?? []).join('\n'),
    ageRange: p?.ageRange ?? '',
    gender: p?.gender ?? '',
    budget: p?.budget ?? '',
    selectionFocus: p?.selectionFocus ?? '',
    domainExpertise: p?.domainExpertise ?? '',
    languageStyle: p?.languageStyle ?? '',
    receptiveness: p?.receptiveness ?? '',
    tags: p?.tags ?? [],
  };
}

/** One item per non-empty line. */
function lines(text: string): string[] {
  return text.split('\n').map((s) => s.trim()).filter(Boolean);
}

function listError(text: string): 'tooMany' | 'tooLong' | null {
  const items = lines(text);
  if (items.length > CONVERSATION_MAX_PERSONA_LIST_ITEMS) return 'tooMany';
  if (items.some((i) => i.length > CONVERSATION_MAX_PERSONA_LIST_ITEM_CHARS)) return 'tooLong';
  return null;
}

/**
 * Create omits empty optionals; edit sends `null` for them so PATCH clears a value the
 * user removed (the API contract: `null` clears an optional).
 */
function buildBody(f: FormState, isEdit: boolean): Record<string, unknown> {
  const empty = isEdit ? null : undefined;
  const opt = (v: string) => (v.trim() ? v.trim() : empty);
  const list = (v: string) => (lines(v).length > 0 ? lines(v) : empty);
  const body: Record<string, unknown> = {
    name: f.name.trim(),
    description: f.description.trim(),
    tone: f.tone,
    decisionCriteria: list(f.decisionCriteria),
    objections: list(f.objections),
    ageRange: opt(f.ageRange),
    gender: opt(f.gender),
    budget: opt(f.budget),
    selectionFocus: opt(f.selectionFocus),
    domainExpertise: opt(f.domainExpertise),
    languageStyle: opt(f.languageStyle),
    receptiveness: opt(f.receptiveness),
    tags: isEdit ? f.tags : f.tags.length > 0 ? f.tags : undefined,
  };
  for (const k of Object.keys(body)) if (body[k] === undefined) delete body[k];
  return body;
}

function errMessage(e: unknown): string {
  if (e instanceof ApiError) return Array.isArray(e.body?.message) ? e.body.message.join(' · ') : e.message;
  return e instanceof Error ? e.message : String(e);
}

export default function PersonasManager({
  brandId,
  limit,
  readOnly,
}: {
  brandId: string;
  /** Per-brand `brand_personas` entitlement; -1 = unlimited, undefined = unknown (hidden). */
  limit?: number;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<ListedPersona[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [editing, setEditing] = useState<ListedPersona | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ListedPersona | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await api.get<ListedPersona[]>(`/brands/${brandId}/brand-personas`);
      setItems(res ?? []);
    } catch (e) {
      setLoadError(errMessage(e));
      setItems((prev) => prev ?? []);
    }
  }, [brandId]);

  useEffect(() => {
    setItems(null);
    setQuery('');
    setTagFilter(null);
    void load();
  }, [load]);

  const allTags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of items ?? []) for (const tag of p.tags ?? []) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [items]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? []).filter((p) => {
      if (tagFilter && !(p.tags ?? []).includes(tagFilter)) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || (p.tags ?? []).some((tag) => tag.includes(q));
    });
  }, [items, query, tagFilter]);

  const count = items?.length ?? 0;
  const hasLimit = typeof limit === 'number' && limit !== -1;
  const atLimit = hasLimit && count >= (limit as number);

  const onSaved = (saved: BrandPersona, isEdit: boolean) => {
    setItems((prev) => {
      const list = prev ?? [];
      if (!isEdit) return [{ ...saved, linkedConversations: 0 }, ...list];
      return list.map((p) => (p.brandPersonaId === saved.brandPersonaId ? { ...saved, linkedConversations: p.linkedConversations } : p));
    });
    setEditing(null);
    setToast(t('brandPersonas.saved'));
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.delete(`/brands/${brandId}/brand-personas/${deleting.brandPersonaId}`);
      setItems((prev) => (prev ?? []).filter((p) => p.brandPersonaId !== deleting.brandPersonaId));
      setDeleting(null);
      setToast(t('brandPersonas.deleted'));
    } catch (e) {
      setDeleteError(errMessage(e));
      // 409 = linked since the list was loaded; refresh the counts.
      if (e instanceof ApiError && e.status === 409) void load();
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {/* ── Toolbar: search · count · add ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Box
          sx={{
            position: 'relative', display: 'flex', alignItems: 'center', width: 240,
            border: `1px solid ${K.line}`, borderRadius: '10px', bgcolor: K.paper,
            '&:focus-within': { borderColor: K.primary, boxShadow: K.focusRing },
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: K.ink3, position: 'absolute', left: 9, pointerEvents: 'none' }} />
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setQuery(''); }}
            placeholder={t('brandPersonas.searchPlaceholder')}
            inputProps={{ 'aria-label': t('brandPersonas.searchPlaceholder') }}
            sx={{ flex: 1, fontSize: 12.5, pl: '29px', pr: query ? '24px' : 1, py: '3px' }}
          />
          {query && (
            <IconButton size="small" aria-label={t('brandKnowledge.list.clearSearch')} onClick={() => setQuery('')} sx={{ position: 'absolute', right: 2, p: '3px', color: K.ink3 }}>
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: K.ink2, fontVariantNumeric: 'tabular-nums' }}>
          {hasLimit ? (
            <><Box component="b" sx={{ color: K.ink, fontWeight: 700 }}>{count}</Box> / {limit}</>
          ) : (
            t('brandPersonas.count', { count })
          )}
        </Typography>
        {!readOnly && (
          <Tooltip title={atLimit ? t('brandPersonas.atLimit', { max: limit }) : ''}>
            <span>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                disabled={atLimit || items === null}
                onClick={() => setEditing('new')}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                {t('brandPersonas.add')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* ── Tag filter ── */}
      {allTags.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            size="small"
            label={t('brandPersonas.filterAll')}
            onClick={() => setTagFilter(null)}
            color={tagFilter === null ? 'primary' : 'default'}
            variant={tagFilter === null ? 'filled' : 'outlined'}
            sx={{ borderRadius: '6px', fontSize: 12 }}
          />
          {allTags.map(([tag, n]) => (
            <Chip
              key={tag}
              size="small"
              label={`${tag} · ${n}`}
              onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
              color={tagFilter === tag ? 'primary' : 'default'}
              variant={tagFilter === tag ? 'filled' : 'outlined'}
              sx={{ borderRadius: '6px', fontSize: 12 }}
            />
          ))}
        </Box>
      )}

      {loadError && (
        <Alert severity="error" action={<Button size="small" onClick={() => void load()}>{t('brandPersonas.retry')}</Button>}>
          {t('brandPersonas.loadError')} {loadError}
        </Alert>
      )}

      {/* ── List ── */}
      <Box sx={{ border: `1px solid ${K.line}`, borderRadius: '10px', overflow: 'hidden', ...markSx }}>
        {items === null ? (
          [0, 1, 2].map((i) => (
            <Box key={i} sx={{ px: 1.75, py: 1.5, borderTop: i ? `1px solid ${K.line}` : 0 }}>
              <Skeleton width="30%" />
              <Skeleton width="70%" />
            </Box>
          ))
        ) : visible.length === 0 ? (
          <Typography sx={{ px: 1.75, py: 2.5, fontSize: 12.5, color: K.ink3, textAlign: 'center' }}>
            {count === 0 ? t('brandPersonas.empty') : t('brandPersonas.noMatch')}
          </Typography>
        ) : (
          visible.map((p, i) => {
            const linked = p.linkedConversations ?? 0;
            return (
              <Box
                key={p.brandPersonaId}
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 1.75, py: 1.25,
                  borderTop: i ? `1px solid ${K.line}` : 0, '&:hover': { bgcolor: K.rowHover },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 650, color: K.ink }}>
                    <Highlight text={p.name} query={query} />
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: K.ink2, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                    {(p.tags ?? []).map((tag) => (
                      <Chip key={tag} size="small" label={tag} sx={{ height: 20, borderRadius: '5px', fontSize: 11, bgcolor: K.chipBg }} />
                    ))}
                    <Typography sx={{ fontSize: 11.5, color: K.ink3, ml: (p.tags ?? []).length ? 0.5 : 0 }}>
                      {linked === 0 ? t('brandPersonas.linkedNone') : t('brandPersonas.linked', { count: linked })}
                    </Typography>
                  </Box>
                </Box>
                {!readOnly && (
                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title={t('brandPersonas.edit')}>
                      <IconButton size="small" aria-label={t('brandPersonas.edit')} onClick={() => setEditing(p)}>
                        <EditOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={linked > 0 ? t('brandPersonas.deleteLinked') : t('brandPersonas.delete')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('brandPersonas.delete')}
                          disabled={linked > 0}
                          onClick={() => { setDeleteError(null); setDeleting(p); }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>

      {editing && (
        <PersonaDialog
          brandId={brandId}
          persona={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}

      <Dialog open={!!deleting} onClose={() => !deleteBusy && setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: K.tight, fontWeight: 700 }}>{t('brandPersonas.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5 }}>{t('brandPersonas.deleteConfirmBody', { name: deleting?.name ?? '' })}</Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 1.5 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>{t('conversations.editorCancel')}</Button>
          <Button color="error" variant="contained" disableElevation onClick={() => void confirmDelete()} disabled={deleteBusy}>
            {deleteBusy ? <CircularProgress size={16} color="inherit" /> : t('brandPersonas.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}

// ── Create / edit dialog ──

function PersonaDialog({
  brandId,
  persona,
  onClose,
  onSaved,
}: {
  brandId: string;
  persona?: BrandPersona;
  onClose: () => void;
  onSaved: (p: BrandPersona, isEdit: boolean) => void;
}) {
  const { t } = useTranslation();
  const isEdit = !!persona;
  const [f, setF] = useState<FormState>(() => formFrom(persona));
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <F extends keyof FormState>(k: F, v: FormState[F]) => setF((p) => ({ ...p, [k]: v }));

  const criteriaErr = listError(f.decisionCriteria);
  const objectionsErr = listError(f.objections);
  const valid = !!f.name.trim() && !!f.description.trim() && !criteriaErr && !objectionsErr;

  const listHelper = (text: string, err: 'tooMany' | 'tooLong' | null) =>
    err === 'tooMany'
      ? t('brandPersonas.listTooMany', { max: CONVERSATION_MAX_PERSONA_LIST_ITEMS })
      : err === 'tooLong'
        ? t('brandPersonas.listItemTooLong', { max: CONVERSATION_MAX_PERSONA_LIST_ITEM_CHARS })
        : t('brandPersonas.listHelper', { n: lines(text).length, max: CONVERSATION_MAX_PERSONA_LIST_ITEMS });

  const commitTag = () => {
    const tag = tagInput.replace(/^-+|-+$/g, '').slice(0, MAX_TAG_CHARS);
    if (tag && !f.tags.includes(tag) && f.tags.length < CONVERSATION_MAX_TAGS) set('tags', [...f.tags, tag]);
    setTagInput('');
  };

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const body = buildBody(f, isEdit);
      const saved = isEdit
        ? await api.patch<BrandPersona>(`/brands/${brandId}/brand-personas/${persona!.brandPersonaId}`, body)
        : await api.post<BrandPersona>(`/brands/${brandId}/brand-personas`, body);
      onSaved(saved, isEdit);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const select = <V extends string>(
    labelKey: string,
    value: V | '',
    onChange: (v: V | '') => void,
    options: { value: V; labelKey: string }[],
    noneKey?: string,
    tooltipKey?: string,
  ) => (
    <FormControl size="small" fullWidth>
      <InputLabel sx={tooltipKey ? { display: 'flex', alignItems: 'center', gap: 0.5 } : undefined}>
        {t(labelKey)}
        {tooltipKey && (
          <Tooltip title={t(tooltipKey)} placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 14, ml: 0.25 }} />
          </Tooltip>
        )}
      </InputLabel>
      <Select label={t(labelKey) + (tooltipKey ? ' i' : '')} value={value} onChange={(e) => onChange(e.target.value as V | '')}>
        <MenuItem value="">{noneKey ? t(noneKey) : '—'}</MenuItem>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{t(o.labelKey)}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography component="span" sx={{ display: 'block', fontFamily: K.tight, fontSize: 18, fontWeight: 700, letterSpacing: '-0.018em' }}>
          {isEdit ? t('brandPersonas.dialogEditTitle') : t('brandPersonas.dialogCreateTitle')}
        </Typography>
        <Typography component="span" sx={{ display: 'block', fontSize: 12.5, color: K.ink2, mt: 0.25 }}>
          {t('brandPersonas.dialogNote')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
            <TextField
              label={t('conversations.editorNameLabel')}
              size="small"
              fullWidth
              required
              value={f.name}
              onChange={(e) => set('name', e.target.value)}
              inputProps={{ maxLength: CONVERSATION_MAX_PERSONA_NAME_CHARS }}
            />
            <FormControl size="small" fullWidth required>
              <InputLabel>{t('conversations.editorToneLabel')}</InputLabel>
              <Select label={t('conversations.editorToneLabel')} value={f.tone} onChange={(e) => set('tone', e.target.value as ConversationTone)}>
                {TONE_KEYS.map((k) => (
                  <MenuItem key={k} value={k}>{t(`conversations.editorTone${k.charAt(0).toUpperCase() + k.slice(1)}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ border: `1px solid ${K.line}`, borderLeft: `4px solid ${K.primary}`, borderRadius: '10px', overflow: 'hidden' }}>
            <Box sx={{ px: 2, pt: 1.5, pb: 1.25, bgcolor: K.primarySoft }}>
              <Typography id="brand-persona-brief-label" sx={{ fontSize: 13.5, fontWeight: 700 }}>
                {t('conversations.editorSituationLabel')} *
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: K.ink2, mt: 0.5, lineHeight: 1.5 }}>
                {t('conversations.editorSituationHelp')}
              </Typography>
              <Typography sx={{ fontSize: 12, color: K.ink3, mt: 0.5, lineHeight: 1.5 }}>
                {t('conversations.editorSituationRule')}
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={f.description}
              onChange={(e) => set('description', e.target.value)}
              inputProps={{ 'aria-labelledby': 'brand-persona-brief-label', maxLength: CONVERSATION_MAX_PERSONA_DESCRIPTION_CHARS }}
              helperText={`${f.description.length} / ${CONVERSATION_MAX_PERSONA_DESCRIPTION_CHARS}`}
              FormHelperTextProps={{ sx: { mx: 2 } }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& fieldset': { border: 'none' } }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label={t('conversations.editorDecisionCriteriaLabel')}
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={f.decisionCriteria}
              onChange={(e) => set('decisionCriteria', e.target.value)}
              placeholder={t('conversations.editorDecisionCriteriaPlaceholder')}
              error={!!criteriaErr}
              helperText={listHelper(f.decisionCriteria, criteriaErr)}
            />
            <TextField
              label={t('brandPersonas.objectionsLabel')}
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={f.objections}
              onChange={(e) => set('objections', e.target.value)}
              placeholder={t('brandPersonas.objectionsPlaceholder')}
              error={!!objectionsErr}
              helperText={listHelper(f.objections, objectionsErr)}
            />
          </Box>

          <Box>
            <Typography sx={{ ...microLabel, mb: 1 }}>
              {t('brandPersonas.optionalTraits')} · {t('conversations.editorTraitGroupProfile')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('conversations.editorAgeLabel')}</InputLabel>
                <Select label={t('conversations.editorAgeLabel')} value={f.ageRange} onChange={(e) => set('ageRange', e.target.value)}>
                  <MenuItem value="">—</MenuItem>
                  {/* A stored value outside the preset list must still render. */}
                  {[...AGE_RANGES, ...(f.ageRange && !AGE_RANGES.includes(f.ageRange) ? [f.ageRange] : [])].map((a) => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {select<Gender>('conversations.editorGenderLabel', f.gender, (v) => set('gender', v), [
                { value: 'female', labelKey: 'conversations.editorGenderFemale' },
                { value: 'male', labelKey: 'conversations.editorGenderMale' },
                { value: 'unspecified', labelKey: 'conversations.editorGenderUnspecified' },
              ])}
              <TextField
                label={t('conversations.editorBudgetLabel')}
                size="small"
                fullWidth
                value={f.budget}
                onChange={(e) => set('budget', e.target.value)}
                inputProps={{ maxLength: CONVERSATION_MAX_PERSONA_BUDGET_CHARS }}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ ...microLabel, mb: 1 }}>
              {t('brandPersonas.optionalTraits')} · {t('conversations.editorTraitGroupBehaviour')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              {select<SelectionFocus>('conversations.editorSelectionFocusLabel', f.selectionFocus, (v) => set('selectionFocus', v), [
                { value: 'broad', labelKey: 'conversations.editorSelectionFocusBroad' },
                { value: 'focused', labelKey: 'conversations.editorSelectionFocusFocused' },
              ], 'conversations.editorSelectionFocusNone', 'conversations.editorSelectionFocusTooltip')}
              {select<DomainExpertise>('conversations.editorDomainExpertiseLabel', f.domainExpertise, (v) => set('domainExpertise', v), [
                { value: 'novice', labelKey: 'conversations.editorDomainExpertiseNovice' },
                { value: 'intermediate', labelKey: 'conversations.editorDomainExpertiseIntermediate' },
                { value: 'expert', labelKey: 'conversations.editorDomainExpertiseExpert' },
              ], 'conversations.editorDomainExpertiseNone', 'conversations.editorDomainExpertiseTooltip')}
              {select<LanguageStyle>('conversations.editorLanguageStyleLabel', f.languageStyle, (v) => set('languageStyle', v), [
                { value: 'professional', labelKey: 'conversations.editorLanguageStyleProfessional' },
                { value: 'casual', labelKey: 'conversations.editorLanguageStyleCasual' },
                { value: 'typos', labelKey: 'conversations.editorLanguageStyleTypos' },
              ], 'conversations.editorLanguageStyleNone', 'conversations.editorLanguageStyleTooltip')}
              {select<Receptiveness>('conversations.editorReceptivenessLabel', f.receptiveness, (v) => set('receptiveness', v), [
                { value: 'accepting', labelKey: 'conversations.editorReceptivenessAccepting' },
                { value: 'curious', labelKey: 'conversations.editorReceptivenessCurious' },
                { value: 'challenger', labelKey: 'conversations.editorReceptivenessChallenger' },
              ], 'conversations.editorReceptivenessNone', 'conversations.editorReceptivenessTooltip')}
            </Box>
          </Box>

          <Box>
            <Typography sx={{ ...microLabel, mb: 1 }}>{t('brandPersonas.tagsLabel')}</Typography>
            {f.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {f.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => set('tags', f.tags.filter((x) => x !== tag))}
                    sx={{ borderRadius: '5px', fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            )}
            {f.tags.length < CONVERSATION_MAX_TAGS && (
              <TextField
                size="small"
                fullWidth
                value={tagInput}
                placeholder={t('brandPersonas.tagsPlaceholder')}
                onChange={(e) => setTagInput(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, MAX_TAG_CHARS))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    commitTag();
                  } else if (e.key === 'Backspace' && tagInput === '' && f.tags.length > 0) {
                    set('tags', f.tags.slice(0, -1));
                  }
                }}
                onBlur={commitTag}
                helperText={t('brandPersonas.tagsHelper', { n: f.tags.length, max: CONVERSATION_MAX_TAGS })}
                FormHelperTextProps={{ sx: { mx: 0 } }}
              />
            )}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>{t('conversations.editorCancel')}</Button>
        <Button variant="contained" disableElevation onClick={() => void save()} disabled={!valid || saving}>
          {saving ? <CircularProgress size={16} color="inherit" /> : t('conversations.editorSave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
